import type { RoofGenerationStageInput, RoofGenerationStageResult } from "building-types/roofs/roofGenerationTypes";
import type { BellyBandBoard } from "building-types/roofs/roofTrimTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { ensureDefined } from "common/utils/assertOfType";
import type { Face } from "geom/brep";
import { polyLine2Copy, polyLine2Edge, polyLine2Extend, polyLine2Reverse } from "geom/polyLine2";
import { getEaveDepth } from "../../../elements/roofTrimUtility";
import { gableSubRoofWallFacesNew } from "../../roofs";
import { buildBellyBandBoardRail } from "../roofBellyBandBoard";
import { resolveRoofWallFramingType } from "../roofSideOverridesUtility";
import { makeRoofWallBackFace, roofWallFacesFromFrontFace } from "../roofWallFaces";

/**
 * Generates the gable end faces of the roof if needed
 */
export function generateRoofWallFaces({
  subRoof,
  subRoofOptions,
}: RoofGenerationStageInput): RoofGenerationStageResult {
  const { roofVolume } = subRoof;
  const {
    startFaces,
    endFaces,
    span,
    rectangularOverhangs,
    perimeters,
    startConnectivity,
    endConnectivity,
    startEdge,
    endEdge,
    rails2D,
    baseHeight,
    heelHeightOffsets,
    eaveEdges,
  } = roofVolume;

  const { topOfWallHeight = baseHeight, roofInputSettings, roofData } = subRoofOptions;
  const { roofSideOverrides } = roofData;
  const { wallSettings, roofFramingSettings, roofSettings } = roofInputSettings;
  const { face: startFrontFace } = startFaces;
  const { face: endFrontFace } = endFaces;
  const { leftOverhang, rightOverhang, baseLeftOverhang, baseRightOverhang } = rectangularOverhangs;
  const { startRail, endRail } = rails2D;
  const { eaveType } = roofSettings;

  const { isFree: isStartFree } = startConnectivity;
  const { isFree: isEndFree } = endConnectivity;
  const { boundingPerimeter } = perimeters;

  // build back faces
  const gableEndWallThickness = wallSettings.exteriorWallBaseThickness;

  let startBackFace: Face | null = null;
  if (startFrontFace) {
    startBackFace = makeRoofWallBackFace(startFrontFace, gableEndWallThickness, span);
  }

  let endBackFace: Face | null = null;
  if (endFrontFace) {
    endBackFace = makeRoofWallBackFace(endFrontFace, gableEndWallThickness, span);
  }

  const startLine = isStartFree && startEdge !== undefined ? polyLine2Edge(boundingPerimeter, startEdge) : undefined;
  const endLine = isEndFree && endEdge !== undefined ? polyLine2Edge(boundingPerimeter, endEdge) : undefined;

  const leftEaveEdge = eaveEdges.find((edge) => edge.side === RoofSide.LEFT);
  const rightEaveEdge = eaveEdges.find((edge) => edge.side === RoofSide.RIGHT);
  const leftEaveLine = leftEaveEdge?.edge;
  const rightEaveLine = rightEaveEdge?.edge;

  const startLineExtended = startLine
    ? polyLine2Extend(polyLine2Copy(startLine), leftOverhang, rightOverhang)
    : undefined;
  const endLineExtended = endLine ? polyLine2Extend(polyLine2Copy(endLine), rightOverhang, leftOverhang) : undefined;

  const startWallFaces = roofWallFacesFromFrontFace(
    resolveRoofWallFramingType(roofFramingSettings, ensureDefined(roofSideOverrides[RoofSide.START]), RoofSide.START),
    startFrontFace,
    startLine ?? null,
    startLineExtended ?? null,
    RoofSide.START,
    true,
    gableEndWallThickness,
  );

  const endWallFaces = roofWallFacesFromFrontFace(
    resolveRoofWallFramingType(roofFramingSettings, ensureDefined(roofSideOverrides[RoofSide.END]), RoofSide.END),
    endFrontFace,
    endLine ?? null,
    endLineExtended ?? null,
    RoofSide.END,
    true,
    gableEndWallThickness,
  );

  // create the wall faces adding the gable end faces
  const wallFaces = gableSubRoofWallFacesNew(startWallFaces, endWallFaces);

  // create belly band board rails
  const soffitDepth = getEaveDepth(roofSettings);
  const baseHeightLeft = baseHeight + heelHeightOffsets.leftOffset - soffitDepth;
  const baseHeightRight = baseHeight + heelHeightOffsets.rightOffset - soffitDepth;
  const bellyBandBoardRails: BellyBandBoard[] = [];

  if (startFrontFace) {
    const bellyBandBoardRail = polyLine2Copy(startRail);
    polyLine2Extend(bellyBandBoardRail, -baseLeftOverhang, -baseRightOverhang);
    polyLine2Reverse(bellyBandBoardRail);

    const bellyBandBoard = buildBellyBandBoardRail(
      bellyBandBoardRail,
      startFrontFace,
      roofSettings,
      topOfWallHeight,
      baseHeight - getEaveDepth(roofSettings),
      eaveType,
      baseRightOverhang,
      baseLeftOverhang,
      baseHeightRight,
      baseHeightLeft,
      roofSideOverrides[RoofSide.START],
      false,
    );

    if (bellyBandBoard) {
      bellyBandBoardRails.push(bellyBandBoard);
    }
  }

  if (endFrontFace) {
    const bellyBandBoardRail = polyLine2Copy(endRail);
    polyLine2Extend(bellyBandBoardRail, -baseLeftOverhang, -baseRightOverhang);

    const bellyBandBoard = buildBellyBandBoardRail(
      bellyBandBoardRail,
      endFrontFace,
      roofSettings,
      topOfWallHeight,
      baseHeight - getEaveDepth(roofSettings),
      eaveType,
      baseLeftOverhang,
      baseRightOverhang,
      baseHeightLeft,
      baseHeightRight,
      roofSideOverrides[RoofSide.END],
      true,
    );

    if (bellyBandBoard) {
      bellyBandBoardRails.push(bellyBandBoard);
    }
  }

  return {
    subRoof: {
      wallEdges: { startLine, endLine, leftEaveLine, rightEaveLine },
      wallFaces,
      bellyBandBoardRails,
    },
    temporaries: {
      startBackFace: startBackFace ?? undefined,
      endBackFace: endBackFace ?? undefined,
    },
  };
}
